import { FaqPage } from './pages/FaqPage';
import { FaqPublicChrome } from './layouts/FaqPublicChrome';
import { useState, useEffect, lazy, Suspense, type ReactNode } from 'react';
import { startMediaSession } from './services/mediaSession';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setupPortalTokenHandoff } from '@platzona/auth-client';
import { isOnCustomDomainHost } from './utils/saasAppHosts';
import { promptServiceWorkerUpdate } from '@platzona/offline';
import { Layout } from './layouts/Layout';
import { SaasRouteFallback } from './components/SaasRouteFallback';
import { ProtectedRoute } from './components/ProtectedRoute';
import { authService } from './services/authService';
import { useLanguageSync } from '@platzona/i18n';
import { PortalBackButton, useAppViewport, usePlatformThemeBootstrap, installPwaForceUpdate, CookieConsentBanner, cookieConsentCopyFromT } from '@platzona/ui';
import { loadAndApplyTheme } from './services/platformThemeService';


/** Suspense for routes *outside* Layout (login / public site / full template preview). */
function Suspend({ children }: { children: ReactNode }) {
  return <Suspense fallback={<SaasRouteFallback fullBleed />}>{children}</Suspense>;
}

/** Builder / customer shell only — Freigabe public sites mount their own editable banner. */
function SaasShellCookieBanner({ onCustomDomain }: { onCustomDomain: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  if (onCustomDomain) return null;
  if (location.pathname.startsWith('/sites/')) return null;
  return <CookieConsentBanner scope="saas" copy={cookieConsentCopyFromT(t)} />;
}

const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })));
const CustomerWorkspacesPage = lazy(() => import('./pages/CustomerWorkspacesPage').then((m) => ({ default: m.CustomerWorkspacesPage })));
const CustomerWorkspaceDetailPage = lazy(() => import('./pages/CustomerWorkspaceDetailPage').then((m) => ({ default: m.CustomerWorkspaceDetailPage })));
const ClientProjectDashboardPage = lazy(() => import('./pages/ClientProjectDashboardPage').then((m) => ({ default: m.ClientProjectDashboardPage })));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })));
const OrganizationsPage = lazy(() => import('./pages/OrganizationsPage').then((m) => ({ default: m.OrganizationsPage })));
const OrgDetailPage = lazy(() => import('./pages/OrgDetailPage').then((m) => ({ default: m.OrgDetailPage })));
const PlansPage = lazy(() => import('./pages/PlansPage').then((m) => ({ default: m.PlansPage })));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage').then((m) => ({ default: m.TemplatesPage })));
const TemplateDetailPage = lazy(() => import('./pages/TemplateDetailPage').then((m) => ({ default: m.TemplateDetailPage })));
const TemplateFullPreviewPage = lazy(() => import('./pages/TemplateFullPreviewPage').then((m) => ({ default: m.TemplateFullPreviewPage })));
const TemplateManagePage = lazy(() => import('./pages/TemplateManagePage').then((m) => ({ default: m.TemplateManagePage })));
const MediaPage = lazy(() => import('./pages/MediaPage').then((m) => ({ default: m.MediaPage })));
const MediaHubPage = lazy(() => import('./pages/MediaHubPage').then((m) => ({ default: m.MediaHubPage })));
const AIToolsPage = lazy(() => import('./pages/AIToolsPage').then((m) => ({ default: m.AIToolsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const CustomerInviteAcceptPage = lazy(() => import('./pages/CustomerInviteAcceptPage').then((m) => ({ default: m.CustomerInviteAcceptPage })));
const PublicSitePage = lazy(() => import('./pages/PublicSitePage').then((m) => ({ default: m.PublicSitePage })));
const CustomDomainSitePage = lazy(() => import('./pages/CustomDomainSitePage'));
const LegalDocumentPage = lazy(() =>
  import('./pages/legal/LegalDocumentPage').then((m) => ({ default: m.LegalDocumentPage })),
);

/** Bare `/collections/...` on LAN has no site slug — restore from last public site visit. */
function PublicCollectionsRedirect() {
  const location = useLocation();
  let remembered = '';
  try {
    remembered = sessionStorage.getItem('platzona.publicSiteSlug') || '';
  } catch {
    remembered = '';
  }
  if (!remembered) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to={`/sites/${remembered}${location.pathname}${location.search}${location.hash}`} replace />;
}

function App() {
  useAppViewport();
  useEffect(() => { installPwaForceUpdate('platzona-saas-v89-20261103-gallery-urls'); }, []);

  useEffect(() => setupPortalTokenHandoff(), []);
  usePlatformThemeBootstrap({
    loadAndApplyTheme,
    isAuthenticated: () => authService.isAuthenticated(),
  });
  useEffect(() => {
    if (authService.isAuthenticated()) {
      authService.hydrateUser().catch(() => {});
    }
  }, []);
  // Presence flag (not the JWT string): silent refresh must not re-run language sync
  // or re-render the whole tree — that contributed to shell flicker.
  const [authPresence, setAuthPresence] = useState(() => (authService.getToken() ? '1' : null));
  useEffect(() => {
    const sync = () => {
      const next = authService.getToken() ? '1' : null;
      setAuthPresence((prev) => (prev === next ? prev : next));
    };
    window.addEventListener('platzona-auth-change', sync);
    return () => window.removeEventListener('platzona-auth-change', sync);
  }, []);
  useEffect(() => startMediaSession(), []);
  useLanguageSync({
    backendUrl: '/api/saas/v1/settings/language',
    getToken: () => authService.getToken(),
    authToken: authPresence,
  });
  const { t } = useTranslation();
  const onCustomDomain = isOnCustomDomainHost();
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);
  useEffect(() => {
    if (import.meta.env.DEV) return undefined;
    return promptServiceWorkerUpdate(() => setSwUpdateAvailable(true));
  }, []);

  return (
    <BrowserRouter>
      {swUpdateAvailable && (
        <div className="layout__sw-update" role="status">
          <span>{t('common.swUpdateAvailable', 'Update available — Refresh')}</span>
          <button type="button" className="layout__sw-update-btn" onClick={() => window.location.reload()}>
            {t('common.swUpdateRefresh', 'Refresh')}
          </button>
        </div>
      )}
      {onCustomDomain ? (
        <Suspend>
          <Routes>
            {/* Kunde invite/login/dashboard render in place on the tenant's own domain —
                every custom domain already proxies /api/ to the same backend, and auth
                tokens are localStorage-scoped per origin (see docs/AGENT-HANDOFF.md). */}
            <Route path="/login" element={<Suspend><LoginPage /></Suspend>} />
            <Route path="/customer-invite/:token" element={<Suspend><CustomerInviteAcceptPage /></Suspend>} />
            {/* Pathless layout route (no `path="/"`) — a path="/" parent trivially
                matches the bare root on its own even with no matching child, which
                rendered an empty Layout shell over every tenant's storefront root. */}
            <Route element={<Layout />}>
              <Route path="/customers" element={<ProtectedRoute><CustomerWorkspacesPage /></ProtectedRoute>} />
              <Route path="/customers/:id" element={<ProtectedRoute><CustomerWorkspaceDetailPage /></ProtectedRoute>} />
              <Route path="/customer/projects/:id" element={<ProtectedRoute><ClientProjectDashboardPage /></ProtectedRoute>} />
              <Route path="/projects/:projectId/media" element={<ProtectedRoute><MediaPage /></ProtectedRoute>} />
            </Route>
            <Route path="/*" element={<CustomDomainSitePage />} />
          </Routes>
        </Suspend>
      ) : (
        <Routes>
          <Route path="/login" element={<Suspend><LoginPage /></Suspend>} />
          <Route path="/customer-invite/:token" element={<Suspend><CustomerInviteAcceptPage /></Suspend>} />
          <Route
            path="/faq"
            element={(
              <FaqPublicChrome>
                <FaqPage />
              </FaqPublicChrome>
            )}
          />
          <Route path="/sites/:slug/*" element={<Suspend><PublicSitePage /></Suspend>} />
          <Route path="/collections/*" element={<PublicCollectionsRedirect />} />
          <Route
            path="/templates/:slug/live/*"
            element={(
              <ProtectedRoute>
                <Suspend><TemplateFullPreviewPage /></Suspend>
              </ProtectedRoute>
            )}
          />
          {/* Layout owns Suspense around <Outlet /> — do not wrap Layout here. */}
          <Route path="/" element={<Layout />}>
            <Route index element={authService.isAuthenticated() ? <ProjectsPage /> : <HomePage />} />
            {/* Builder-host legal only — Freigabe custom domains keep tenant site catch-all */}
            <Route path="legal/:docSlug" element={<LegalDocumentPage />} />
            <Route path="impressum" element={<LegalDocumentPage />} />
            <Route path="datenschutz" element={<LegalDocumentPage />} />
            <Route path="privacy" element={<LegalDocumentPage />} />
            <Route path="agb" element={<LegalDocumentPage />} />
            <Route path="terms" element={<LegalDocumentPage />} />
            <Route path="cookies" element={<LegalDocumentPage />} />
            <Route path="freigabe" element={<LegalDocumentPage />} />
            <Route path="widerruf" element={<LegalDocumentPage />} />
            <Route path="customers" element={<ProtectedRoute><CustomerWorkspacesPage /></ProtectedRoute>} />
            <Route path="customers/:id" element={<ProtectedRoute><CustomerWorkspaceDetailPage /></ProtectedRoute>} />
            <Route path="customer/projects/:id" element={<ProtectedRoute><ClientProjectDashboardPage /></ProtectedRoute>} />
            <Route path="projects/:id" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
            <Route path="orgs" element={<ProtectedRoute><OrganizationsPage /></ProtectedRoute>} />
            <Route path="orgs/:id" element={<ProtectedRoute><OrgDetailPage /></ProtectedRoute>} />
            <Route path="plans" element={<ProtectedRoute><PlansPage /></ProtectedRoute>} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="templates/manage/:slug" element={<ProtectedRoute><TemplateManagePage /></ProtectedRoute>} />
            <Route path="templates/:slug" element={<TemplateDetailPage />} />
            <Route path="projects/:projectId/media" element={<ProtectedRoute><MediaPage /></ProtectedRoute>} />
            <Route path="media/personal" element={<ProtectedRoute><MediaPage personalMode /></ProtectedRoute>} />
            <Route path="media" element={<ProtectedRoute><MediaHubPage /></ProtectedRoute>} />
            <Route path="ai" element={<ProtectedRoute><AIToolsPage /></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          </Route>
        </Routes>
      )}
      <PortalBackButton />
      <SaasShellCookieBanner onCustomDomain={onCustomDomain} />
    </BrowserRouter>
  );
}

export default App;
